import React, { useState } from 'react';
import { toast } from 'sonner';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button, Input, Textarea } from '@/shared/components/ui';
import type { PopulatedLead } from '../types';
import { Icon } from '@/shared/icons';
/**
 * Props do LoseLeadDialog
 */
export interface LoseLeadDialogProps {
  /** Lead a ser marcado como perdido */
  lead: PopulatedLead | null;
  /** Controla se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Callback para confirmar e enviar dados */
  onConfirm: (data: LoseLeadData) => void;
  /** Estado de loading durante envio */
  loading?: boolean;
}

/**
 * Dados para marcar lead como perdido
 */
export interface LoseLeadData {
  lostReason: string;
  lostDate: string;  // ISO datetime string
  businessStatus: 'lost';
}

/**
 * Modal para marcar lead como perdido
 *
 * Chrome = Modal shared (F72). Coleta motivo da perda e data da perda.
 * Permite registrar perdas retroativas.
 *
 * @example
 * ```tsx
 * <LoseLeadDialog
 *   lead={selectedLead}
 *   isOpen={isLoseDialogOpen}
 *   onClose={() => setIsLoseDialogOpen(false)}
 *   onConfirm={handleLoseLead}
 *   loading={isUpdating}
 * />
 * ```
 */
export const LoseLeadDialog: React.FC<LoseLeadDialogProps> = ({
  lead,
  isOpen,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [lostReason, setLostReason] = useState<string>('');
  const [lostDate, setLostDate] = useState<string>(
    new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:mm
  );

  // Não renderizar se não houver lead
  if (!isOpen || !lead) return null;

  const handleSubmit = () => {
    // Validações
    if (!lostReason.trim()) {
      toast.error('Motivo da perda é obrigatório');
      return;
    }

    if (!lostDate) {
      toast.error('Data da perda é obrigatória');
      return;
    }

    // Converter data local para ISO datetime
    const lostDateISO = new Date(lostDate).toISOString();

    onConfirm({
      lostReason: lostReason.trim(),
      lostDate: lostDateISO,
      businessStatus: 'lost'
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md">
      <ModalHeader onClose={handleClose}>
        <div
          className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
          style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}
        >
          <Icon name="alert-circle" className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
          Marcar como Perdido
        </h2>
      </ModalHeader>

      <ModalContent className="space-y-4">
        {/* Lead Info */}
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: '#f1f5f9',
            borderColor: '#e5e7eb',
          }}
        >
          <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
            {lead.contact?.name || 'Sem nome'}
          </p>
          <p className="text-xs" style={{ color: '#64748b' }}>
            {lead.segment}
          </p>
          {lead.budget && (
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Orçamento estimado: {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(lead.budget)}
            </p>
          )}
        </div>

        {/* Motivo da Perda */}
        <div>
          <Textarea
            label="Motivo da Perda"
            required
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            placeholder="Descreva o motivo da perda..."
            rows={4}
            resize="none"
            description="Descreva o motivo da perda para análise futura"
            disabled={loading}
            data-testid="lose-lead-reason"
          />
        </div>

        {/* Data da Perda */}
        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: '#1e293b' }}
          >
            Data da Perda *
          </label>
          <Input
            type="datetime-local"
            value={lostDate}
            onChange={(e) => setLostDate(e.target.value)}
            icon={'calendar'}
            disabled={loading}
            data-testid="lose-lead-date"
          />
          <p className="text-xs mt-1" style={{ color: '#64748b' }}>
            Data em que foi confirmada a perda (pode ser retroativa)
          </p>
        </div>

        {/* Warning box */}
        <div
          className="p-3 rounded-lg text-xs"
          style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fde047',
            color: '#92400e'
          }}
        >
          <p>
            <strong>Atenção:</strong> Ao confirmar, o status do negócio será alterado para "Perdido". Esta ação pode ser revertida posteriormente se necessário.
          </p>
        </div>
      </ModalContent>

      <ModalFooter>
        <Button
          onClick={handleClose}
          variant="secondary"
          size="md"
          disabled={loading}
          data-testid="lose-lead-cancel"
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          variant="error"
          size="md"
          disabled={loading}
          data-testid="lose-lead-confirm"
        >
          {loading ? 'Salvando...' : 'Confirmar Perda'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
